// src/components/rpa/agent/WorkflowStatusCard.js
// Shows current workflow status when a document is selected.
// Each completed step has a "Load Anomalies" button that calls
// POST /admin/rpa/agent/extract-anomalies and renders AnomalyEscalationCard inline.

import React, { useState } from 'react';
import axios from 'axios';
import AnomalyEscalationCard from './AnomalyEscalationCard';
import './WorkflowStatusCard.css';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

// Steps that can yield anomalies — show the Load Anomalies button for these
const ANOMALY_STEPS = new Set([
  'upload_bank_statements',
  'upload_bank_reconciliation',
  'upload_ar_confirmations',
  'upload_ap_confirmations',
  'upload_inventory_confirmations',
  'upload_inventory_observation',
  'assess_materiality',
  'conduct_substantive_testing',
  'conduct_audit_testing',
  'generate_findings',
]);

const STEP_LABELS = {
  classify:                        'Classify Document',
  assign_workflow:                 'Assign Workflow',
  analyze_financial_statement:     'Analyze Financial Statement',
  extract_balances:                'Extract Balances',
  assess_materiality:              'Assess Materiality',
  upload_trial_balance:            'Upload Trial Balance',
  upload_income_statement:         'Upload Income Statement',
  upload_bank_statements:          'Upload Bank Statements',
  upload_bank_reconciliation:      'Upload Bank Reconciliation',
  upload_bank_confirmations:       'Upload Bank Confirmations',
  upload_ar_confirmations:         'Upload AR Confirmations',
  upload_ap_confirmations:         'Upload AP Confirmations',
  upload_inventory_confirmations:  'Upload Inventory Confirmations',
  upload_inventory_observation:    'Physical Inventory Observation',
  upload_supporting_documents:     'Upload Supporting Documents',
  test_assertions:                 'Test Assertions',
  conduct_substantive_testing:     'Conduct Substantive Testing',
  formulate_audit_procedures:      'Formulate Audit Procedures',
  conduct_audit_testing:           'Conduct Audit Testing',
  generate_findings:               'Generate Audit Findings',
  generate_workpapers:             'Generate Workpapers',
};

const WorkflowStatusCard = ({ doc, docId }) => {
  const [anomalyState, setAnomalyState] = useState({}); // stepId → { loading, anomalies }

  const steps    = doc?.workflowAssignment?.steps || [];
  const template = doc?.workflowAssignment?.templateId || '';

  const completed = steps.filter(s => s.status === 'completed').length;
  const total     = steps.length;
  const pct       = total ? Math.round((completed / total) * 100) : 0;
  const isComplete = completed === total && total > 0;

  const loadAnomalies = async (stepId) => {
    setAnomalyState(prev => ({ ...prev, [stepId]: { loading: true, anomalies: null } }));
    try {
      const res = await axios.post(
        `${API_URL}/admin/rpa/agent/extract-anomalies`,
        { docId, stepId },
        { withCredentials: true }
      );
      // ⭐ Human-in-the-Loop: blind mode — server withholds findings anomalies
      // until independent auditor findings are submitted for this document.
      if (res.data?.blocked) {
        setAnomalyState(prev => ({
          ...prev,
          [stepId]: { loading: false, anomalies: [], blocked: true, blockedMessage: res.data?.message },
        }));
        return;
      }
      setAnomalyState(prev => ({
        ...prev,
        [stepId]: { loading: false, anomalies: res.data?.anomalies || [] },
      }));
    } catch (_) {
      setAnomalyState(prev => ({ ...prev, [stepId]: { loading: false, anomalies: [] } }));
    }
  };

  if (!steps.length) return null;

  return (
    <div className="wsc-card">
      <div className="wsc-header">
        <div className="wsc-title">
          {isComplete ? '🎉 Audit Complete' : '📋 Workflow Progress'}
        </div>
        <div className="wsc-meta">
          {doc?.originalName || doc?.fileName || 'Document'}
        </div>
        <div className="wsc-progress-bar">
          <div className="wsc-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="wsc-pct">{completed}/{total} steps complete ({pct}%)</div>
      </div>

      <div className="wsc-steps">
        {steps.map((s) => {
          const isDone    = s.status === 'completed';
          const isRunning = s.status === 'in_progress';
          const label     = STEP_LABELS[s.id] || s.name || s.id;
          const canAnomaly= isDone && ANOMALY_STEPS.has(s.id);
          const aState    = anomalyState[s.id];

          return (
            <div key={s.id} className={`wsc-step ${isDone ? 'done' : isRunning ? 'running' : 'pending'}`}>
              <div className="wsc-step-row">
                <span className="wsc-step-icon">
                  {isDone ? '✅' : isRunning ? '⏳' : '⏸'}
                </span>
                <span className="wsc-step-label">{label}</span>
                {canAnomaly && !aState && (
                  <button
                    className="wsc-anomaly-btn"
                    onClick={() => loadAnomalies(s.id)}
                  >
                    🔍 Anomalies
                  </button>
                )}
                {canAnomaly && aState?.loading && (
                  <span className="wsc-loading">checking…</span>
                )}
                {canAnomaly && aState && !aState.loading && aState.blocked && (
                  <span
                    className="wsc-clear"
                    style={{ color: '#6d28d9' }}
                    title={aState.blockedMessage || 'AI findings are hidden until independent auditor findings are submitted.'}
                  >
                    🔒 Hidden until independent findings submitted
                  </span>
                )}
                {canAnomaly && aState && !aState.loading && !aState.blocked && aState.anomalies?.length === 0 && (
                  <span className="wsc-clear">✓ No anomalies</span>
                )}
              </div>
              {aState?.anomalies?.length > 0 && (
                <div className="wsc-anomaly-wrap">
                  <AnomalyEscalationCard
                    docId={docId}
                    stepId={s.id}
                    stepLabel={label}
                    anomalies={aState.anomalies}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isComplete && (
        <div className="wsc-complete-msg">
          All audit steps complete. Ask me to draft the management letter, review anomalies above, or add issues.
        </div>
      )}
    </div>
  );
};

export default WorkflowStatusCard;